import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Download, PenLine, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ModuleTitleLIFT } from "@/components/lift/ModuleTitleLIFT";

export default function PacketPage() {
  const { cycleId, employeeId } = useParams();
  const { user } = useAuth();
  const [packet, setPacket] = useState(null);
  const [typedName, setTypedName] = useState("");
  const [consent, setConsent] = useState(false);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/lift/synthesis/${cycleId}/${employeeId}`);
      setPacket(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleId, employeeId]);

  const isSelf = user.id === employeeId;
  const acknowledged = !!packet?.acknowledgment?.timestamp;
  const released = !!packet?.packet_released_at;

  const acknowledge = async () => {
    if (!consent) {
      toast.error("Please confirm the acknowledgement.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/lift/synthesis/${cycleId}/${employeeId}/acknowledge`, {
        typed_name: typedName,
        consent,
        comment,
      });
      toast.success("Acknowledgment recorded — thank you.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/lift/synthesis/${cycleId}/${employeeId}/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `LIFT-${packet?.employee?.name || "packet"}.pdf`.replace(/\s+/g, "-");
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  if (!packet) return <p className="italic text-charcoal-muted">Loading packet…</p>;
  const agg = packet.peer_aggregate || {};

  return (
    <div className="space-y-6" data-testid="lift-packet-page">
      <Link to={isSelf ? `/lift/cycle/${cycleId}` : `/lift/manager/${cycleId}`} className="patria-btn-ghost -ml-3">
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> Back
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <ModuleTitleLIFT />
          <h1 className="mt-3 text-4xl font-serif text-forest-900">
            {isSelf ? "My Review Packet" : `Review Packet · ${packet.employee?.name}`}
          </h1>
          <p className="text-sm text-charcoal-muted">{packet.cycle_name}</p>
        </div>
        {released && (
          <button className="patria-btn-secondary" onClick={downloadPdf} data-testid="packet-download-pdf">
            <Download className="h-4 w-4" strokeWidth={1.5} /> Download PDF
          </button>
        )}
      </div>

      {!released && (
        <div className="patria-card p-6">
          <p className="text-charcoal-soft">
            Your packet has not been released yet. Your manager will complete their synthesis
            and share it with you in a review conversation.
          </p>
        </div>
      )}

      {released && (
        <>
          <PacketSection title="Synthesis">
            <p className="whitespace-pre-wrap font-serif text-base leading-relaxed text-charcoal">
              {packet.synthesis_summary || <span className="italic text-charcoal-muted">(no summary provided)</span>}
            </p>
          </PacketSection>

          <PacketSection title="Self-reflection">
            {packet.self_reflection?.length ? (
              packet.self_reflection.map((a) => <AnswerRow key={a.question_id} a={a} />)
            ) : (
              <p className="italic text-charcoal-muted">Not submitted.</p>
            )}
          </PacketSection>

          <PacketSection title="Manager evaluation">
            {packet.manager_eval?.hidden_reason ? (
              <p className="italic text-charcoal-muted">{packet.manager_eval.hidden_reason}</p>
            ) : (
              <>
                {packet.manager_eval?.answers?.map((a) => <AnswerRow key={a.question_id} a={a} />)}
                <Labeled label="Strengths" value={packet.manager_eval?.strengths} />
                <Labeled label="Opportunities" value={packet.manager_eval?.opportunities} />
              </>
            )}
          </PacketSection>

          <PacketSection title="Peer feedback (anonymized)">
            {agg.suppressed ? (
              <p className="italic text-terracotta-600">{agg.suppressed_note}</p>
            ) : (
              <div className="space-y-4">
                <p className="text-xs text-charcoal-muted">{agg.response_count} responses</p>
                {agg.strengths_bullets?.length > 0 && (
                  <div>
                    <p className="patria-chip-green">Strengths themes</p>
                    <ul className="mt-2 list-disc pl-5 text-sm space-y-1">
                      {agg.strengths_bullets.map((b) => <li key={`s-${b}`}>{b}</li>)}
                    </ul>
                  </div>
                )}
                {agg.growth_bullets?.length > 0 && (
                  <div>
                    <p className="patria-chip-terra">Growth themes</p>
                    <ul className="mt-2 list-disc pl-5 text-sm space-y-1">
                      {agg.growth_bullets.map((b) => <li key={`g-${b}`}>{b}</li>)}
                    </ul>
                  </div>
                )}
                {agg.ratings?.length > 0 && (
                  <div className="mt-3 overflow-hidden rounded-2xl shadow-card">
                    <table className="patria-table w-full">
                      <thead>
                        <tr><th>Question</th><th>Average</th><th>Responses</th></tr>
                      </thead>
                      <tbody>
                        {agg.ratings.map((r) => (
                          <tr key={r.question_id}>
                            <td>{r.question_text}</td>
                            <td>{r.avg}</td>
                            <td>{r.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </PacketSection>

          {/* Acknowledgment */}
          {isSelf && (
            <PacketSection title="Your acknowledgment" testId="packet-ack-section">
              {acknowledged ? (
                <div className="rounded-2xl border border-forest-600/20 bg-forest-600/5 p-5">
                  <p className="flex items-center gap-2 text-forest-800 font-medium">
                    <ShieldCheck className="h-4 w-4" strokeWidth={1.5} />
                    Acknowledged on {packet.acknowledgment.timestamp}
                  </p>
                  <p className="mt-1 text-sm text-charcoal-soft">
                    Signed as <span className="font-medium">{packet.acknowledgment.typed_name}</span>
                  </p>
                  {packet.acknowledgment.comment && (
                    <p className="mt-3 text-sm text-charcoal whitespace-pre-wrap">
                      &ldquo;{packet.acknowledgment.comment}&rdquo;
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1">
                      Written comments (optional, preserved permanently)
                    </label>
                    <textarea
                      className="patria-input w-full min-h-[100px]"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      data-testid="packet-ack-comment"
                    />
                  </div>
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      data-testid="packet-ack-checkbox"
                    />
                    <span>
                      I confirm that I have reviewed this LIFT packet with my manager. I understand
                      that acknowledging does not indicate agreement — it records receipt.
                    </span>
                  </label>
                  <div>
                    <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1">
                      Type your full name exactly as it appears on your profile ({user.name})
                    </label>
                    <input
                      className="patria-input w-full sm:max-w-md"
                      value={typedName}
                      onChange={(e) => setTypedName(e.target.value)}
                      data-testid="packet-ack-name"
                    />
                  </div>
                  <button
                    className="patria-btn-primary"
                    onClick={acknowledge}
                    disabled={submitting || !consent || !typedName.trim()}
                    data-testid="packet-ack-submit"
                  >
                    <PenLine className="h-4 w-4" strokeWidth={1.5} /> Sign & acknowledge
                  </button>
                </div>
              )}
            </PacketSection>
          )}
        </>
      )}
    </div>
  );
}

function PacketSection({ title, children, testId }) {
  return (
    <section className="patria-card p-6" data-testid={testId}>
      <h3 className="text-xl font-serif text-forest-900 mb-3">{title}</h3>
      {children}
    </section>
  );
}

function AnswerRow({ a }) {
  return (
    <div className="mb-3">
      <p className="text-xs uppercase tracking-widest text-charcoal-muted">{a.question_text}</p>
      <p className="text-sm text-charcoal whitespace-pre-wrap">
        {a.type === "rating_5" ? `${a.value || 0} / 5` : (a.value || <span className="italic text-charcoal-muted">not answered</span>)}
      </p>
    </div>
  );
}

function Labeled({ label, value }) {
  return (
    <div className="mt-3">
      <p className="text-xs uppercase tracking-widest text-charcoal-muted">{label}</p>
      <p className="text-sm text-charcoal whitespace-pre-wrap">{value || <span className="italic text-charcoal-muted">(none)</span>}</p>
    </div>
  );
}
